if (!customElements.get('golden-shoe-wall')) {
  customElements.define('golden-shoe-wall', class extends HTMLElement {
    connectedCallback() {
      if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',()=>{if(this.isConnected)this.initialize()},{once:true});return;}
      this.initialize();
    }
    initialize() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = false;
      this.motionFactor = this.motion.matches ? .35 : 1;
      this.finePointer = matchMedia('(hover:hover) and (pointer:fine)');
      this.visible = !('IntersectionObserver' in window);
      this.hovered = false;
      this.focused = false;
      this.drag = null;
      this.lastTime = 0;
      this.suppressClickUntil = 0;
      this.button = this.querySelector('[data-wall-toggle]');
      this.lanes = [...this.querySelectorAll('[data-wall-lane]')].map(element => {
        const group = element.querySelector('[data-wall-group]');
        element.scrollLeft = 0;
        group.parentElement.append(this.makeCopy(group));
        return { element, group, track: group.parentElement, position: 0, width: 0, speed: Number(element.dataset.speed), direction: Number(element.dataset.direction) };
      });
      this.dataset.enhanced = '';
      if (this.button) this.button.hidden = false;
      this.querySelector('[data-wall-hint]').hidden = false;
      this.measure();
      this.updateButton();
      this.button?.addEventListener('click', () => { this.paused = !this.paused; this.updateButton(); this.schedule(); }, options);
      this.motion.addEventListener('change', event => { this.motionFactor = event.matches ? .35 : 1; this.schedule(); }, options);
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && this.finePointer.matches) { this.hovered = true; this.schedule(); } }, options);
      this.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') { this.hovered = false; this.schedule(); } }, options);
      this.addEventListener('focusin', event => {
        const card = event.target.closest('[data-wall-card]');
        this.focused = Boolean(card && card.matches(':focus-visible'));
        if (card && card.matches(':focus-visible')) {
          const lane = this.lanes.find(item => item.element.contains(card));
          const rect = card.getBoundingClientRect();
          const viewport = lane.element.getBoundingClientRect();
          if (rect.left < viewport.left + 8 || rect.right > viewport.right - 8) {
            lane.position += rect.left - viewport.left - (viewport.width - rect.width) / 2;
            lane.position = Math.max(0, Math.min(lane.position, Math.max(0, lane.width - viewport.width)));
            this.paint(lane);
          }
        }
        this.schedule();
      }, options);
      this.addEventListener('focusout', () => queueMicrotask(() => { if (this.isConnected) { this.focused = Boolean(this.querySelector('[data-wall-card]:focus-visible')); this.schedule(); } }), options);
      this.lanes.forEach(lane => {
        lane.element.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button !== 0) return;
          this.drag = { lane, id: event.pointerId, x: event.clientX, y: event.clientY, position: lane.position, moved: false };
          this.schedule();
        }, options);
        lane.element.addEventListener('pointermove', event => {
          const drag = this.drag;
          if (!drag || drag.id !== event.pointerId || drag.lane !== lane) return;
          const dx = event.clientX - drag.x;
          const dy = event.clientY - drag.y;
          if (!drag.moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            drag.moved = true;
            lane.element.dataset.dragging = '';
            lane.element.setPointerCapture(event.pointerId);
          }
          if (drag.moved) {
            event.preventDefault();
            lane.position = drag.position - dx;
            this.paint(lane);
          }
        }, options);
      });
      const endDrag = event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const { lane, moved, id } = this.drag;
        this.drag = null;
        delete lane.element.dataset.dragging;
        if (lane.element.hasPointerCapture(id)) lane.element.releasePointerCapture(id);
        if (moved) this.suppressClickUntil = performance.now() + 350;
        this.schedule();
      };
      window.addEventListener('pointerup', endDrag, options);
      window.addEventListener('pointercancel', endDrag, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-wall-card]') && performance.now() < this.suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
      }, { capture: true, signal: this.abort.signal });
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      window.addEventListener('pageshow', () => {
        this.drag = null; this.hovered = false;
        this.focused = Boolean(this.querySelector('[data-wall-card]:focus-visible'));
        const rect = this.getBoundingClientRect();
        this.visible = rect.bottom > 0 && rect.top < window.innerHeight;
        this.measure(); this.schedule();
      }, options);
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => this.measure());
        this.lanes.forEach(lane => { this.resize.observe(lane.group); this.resize.observe(lane.element); });
      } else window.addEventListener('resize', () => this.measure(), options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { this.visible = entries.some(entry => entry.isIntersecting); this.schedule(); });
        this.observer.observe(this);
      }
      this.schedule();
    }
    makeCopy(group) {
      const copy = group.cloneNode(true);
      copy.removeAttribute('data-wall-group');
      copy.removeAttribute('aria-label');
      copy.setAttribute('aria-hidden', 'true');
      copy.dataset.wallCopy = '';
      copy.querySelectorAll('a').forEach(link => link.tabIndex = -1);
      return copy;
    }
    measure() {
      this.lanes.forEach(lane => {
        const width = lane.group.getBoundingClientRect().width;
        if (lane.width && width) lane.position = lane.position / lane.width * width;
        lane.width = width;
        if (width) {
          const copies = Math.max(1, Math.ceil(lane.element.getBoundingClientRect().width / width));
          while (lane.track.children.length < copies + 1) lane.track.append(this.makeCopy(lane.group));
          while (lane.track.children.length > copies + 1) lane.track.lastElementChild.remove();
        }
        this.paint(lane);
      });
    }
    paint(lane) {
      if (!lane.width) return;
      lane.position = ((lane.position % lane.width) + lane.width) % lane.width;
      lane.track.style.transform = `translate3d(${-lane.position}px,0,0)`;
    }
    updateButton() {
      if (!this.button) return;
      this.button.setAttribute('aria-pressed', String(this.paused));
      this.button.querySelector('[data-wall-toggle-label]').textContent = this.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastTime = 0;
      if (this.ready && !this.paused && this.visible && !this.hovered && !this.focused && !this.drag && !document.hidden) this.frame = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      const elapsed = this.lastTime ? Math.min((time - this.lastTime) / 1000, .05) : 0;
      this.lastTime = time;
      this.lanes.forEach(lane => { lane.position += elapsed * lane.speed * lane.direction * this.motionFactor; this.paint(lane); });
      this.frame = requestAnimationFrame(next => this.tick(next));
    }
    disconnectedCallback() {
      this.ready = false;
      this.abort?.abort();
      this.resize?.disconnect();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.drag = null;
      this.querySelectorAll('[data-wall-copy]').forEach(copy => copy.remove());
      this.querySelectorAll('[data-wall-track]').forEach(track => track.style.removeProperty('transform'));
      delete this.dataset.enhanced;
    }
  });
}
